import { NSARouterMakerResult } from "namirasoft-account-react";
import { SortItem } from "namirasoft-core";
import { BudgetRow, GuardRailRow, KnowledgebaseRow, MemoryRow, ValidatorRow } from "namirasoft-inference";
import { LogGroupRow } from "namirasoft-log";
import { NamirasoftMap } from "namirasoft-site-map";
import { IBaseComponentProps, NSBox, NSBoxEntity, NSBoxInteger, NSColumn, NSRepeater, NSRow } from "namirasoft-site-react";
import { Component, createRef } from "react";
import { InferenceRouterProps } from "../InferenceRouterProps";
import Styles from "./ChatOptions.module.css";
import ChatTagLine, { ChatTagValue } from "./ChatTagLine";

export interface ChatOptionsValue
{
    memory_limit: number | null;
    validator_id: string | null;
    budget_ids: string[];
    memory_ids: string[];
    log_group_id: string | null;
    knowledgebase_ids: string[];
    guardrail_ids: string[];
    tags: ChatTagValue[];
}

export interface ChatOptionsProps extends IBaseComponentProps, NSARouterMakerResult, InferenceRouterProps
{
    disabled?: boolean;
}

interface ChatOptionsState
{
    open: boolean;
    mounted: boolean;
}

export default class ChatOptions extends Component<ChatOptionsProps, ChatOptionsState>
{
    static EMPTY(): ChatOptionsValue
    {
        return {
            memory_limit: null,
            validator_id: null,
            budget_ids: [],
            memory_ids: [],
            log_group_id: null,
            knowledgebase_ids: [],
            guardrail_ids: [],
            tags: [],
        };
    }

    private NSBoxInteger_MemoryLimit = createRef<NSBoxInteger>();
    private NSBoxEntity_Validator = createRef<NSBoxEntity<ValidatorRow>>();
    private NSBoxEntity_LogGroup = createRef<NSBoxEntity<LogGroupRow>>();
    private NSBoxEntity_Budgets = createRef<NSBoxEntity<BudgetRow>>();
    private NSBoxEntity_Memories = createRef<NSBoxEntity<MemoryRow>>();
    private NSBoxEntity_Knowledgebases = createRef<NSBoxEntity<KnowledgebaseRow>>();
    private NSBoxEntity_GuardRails = createRef<NSBoxEntity<GuardRailRow>>();
    private NSRepeater_Tags = createRef<NSRepeater<ChatTagLine, ChatTagValue>>();

    constructor(props: ChatOptionsProps)
    {
        super(props);
        this.state = { open: false, mounted: false };
        this.getValue = this.getValue.bind(this);
        this.onToggle = this.onToggle.bind(this);
        this.onChanged = this.onChanged.bind(this);
    }

    getValue(checkError: boolean = false): ChatOptionsValue
    {
        if (!this.state.mounted)
            return ChatOptions.EMPTY();
        return {
            memory_limit: this.NSBoxInteger_MemoryLimit.current?.getValue(checkError) ?? null,
            validator_id: this.NSBoxEntity_Validator.current?.getValueOne(checkError) ?? null,
            budget_ids: this.NSBoxEntity_Budgets.current?.getValueAll() ?? [],
            memory_ids: this.NSBoxEntity_Memories.current?.getValueAll() ?? [],
            log_group_id: this.NSBoxEntity_LogGroup.current?.getValueOne(checkError) ?? null,
            knowledgebase_ids: this.NSBoxEntity_Knowledgebases.current?.getValueAll() ?? [],
            guardrail_ids: this.NSBoxEntity_GuardRails.current?.getValueAll() ?? [],
            tags: this.NSRepeater_Tags.current?.getValue(checkError) ?? [],
        };
    }

    setValue(value: Partial<ChatOptionsValue>)
    {
        this.NSBoxInteger_MemoryLimit.current?.setValue(value.memory_limit ?? null);
        this.NSBoxEntity_Validator.current?.setValue(value.validator_id ?? null);
        this.NSBoxEntity_LogGroup.current?.setValue(value.log_group_id ?? null);
        this.NSBoxEntity_Budgets.current?.setValueAll(value.budget_ids ?? []);
        this.NSBoxEntity_Memories.current?.setValueAll(value.memory_ids ?? []);
        this.NSBoxEntity_Knowledgebases.current?.setValueAll(value.knowledgebase_ids ?? []);
        this.NSBoxEntity_GuardRails.current?.setValueAll(value.guardrail_ids ?? []);
        this.NSRepeater_Tags.current?.setValue(value.tags ?? []);
    }

    private getCount(): number
    {
        let value = this.getValue(false);
        let count = 0;
        if (value.memory_limit != null) count++;
        if (value.validator_id) count++;
        if (value.log_group_id) count++;
        count += value.budget_ids.length;
        count += value.memory_ids.length;
        count += value.knowledgebase_ids.length;
        count += value.guardrail_ids.length;
        count += value.tags.length;
        return count;
    }

    private onToggle()
    {
        this.setState(prev => ({ open: !prev.open, mounted: prev.mounted || !prev.open }));
    }

    private onChanged()
    {
        this.forceUpdate();
    }

    override render()
    {
        let namirasoft = new NamirasoftMap(this.props.account.token_manager, console.error);
        let count = this.getCount();
        return (
            <div className={Styles.ns_chat_options}>
                <button
                    type="button"
                    className={Styles.ns_chat_options_toggle}
                    onClick={this.onToggle}
                >
                    <span className={Styles.ns_chat_options_arrow}>{this.state.open ? "▾" : "▸"}</span>
                    Options
                    {count > 0 && <span className={Styles.ns_chat_options_badge}>{count}</span>}
                </button>
                {
                    this.state.mounted &&
                    <div className={Styles.ns_chat_options_panel} hidden={!this.state.open}>
                        <NSColumn>
                            <NSRow>
                                <NSBoxEntity<ValidatorRow>
                                    ref={this.NSBoxEntity_Validator}
                                    {...this.props}
                                    title="Validator"
                                    required={false}
                                    multiple={false}
                                    clearable
                                    table={namirasoft.inference.tables.validator}
                                    getValue={(item) => item.id}
                                    getTitle={(item) => item.name}
                                    getSort={() => new SortItem(namirasoft.inference.tables.validator, namirasoft.inference.tables.validator.columns.name, true)}
                                    onChanged={this.onChanged}
                                    style={{ width: NSBox.width.one }}
                                />
                                <NSBoxEntity<LogGroupRow>
                                    ref={this.NSBoxEntity_LogGroup}
                                    {...this.props}
                                    title="Log Group"
                                    required={false}
                                    multiple={false}
                                    clearable
                                    table={namirasoft.log.tables.log_group}
                                    getValue={(item) => item.id}
                                    getTitle={(item) => item.name}
                                    getSort={() => new SortItem(namirasoft.log.tables.log_group, namirasoft.log.tables.log_group.columns.name, true)}
                                    onChanged={this.onChanged}
                                    style={{ width: NSBox.width.one }}
                                />
                                <NSBoxEntity<GuardRailRow>
                                    ref={this.NSBoxEntity_GuardRails}
                                    {...this.props}
                                    title="Guard Rails"
                                    required={false}
                                    multiple
                                    clearable
                                    table={namirasoft.inference.tables.guard_rail}
                                    getValue={(item) => item.id}
                                    getTitle={(item) => item.name}
                                    getSort={() => new SortItem(namirasoft.inference.tables.guard_rail, namirasoft.inference.tables.guard_rail.columns.name, true)}
                                    onChanged={this.onChanged}
                                    style={{ width: NSBox.width.double }}
                                />
                            </NSRow>
                            <NSRow>
                                <NSBoxEntity<MemoryRow>
                                    ref={this.NSBoxEntity_Memories}
                                    {...this.props}
                                    title="Memories"
                                    required={false}
                                    multiple
                                    clearable
                                    table={namirasoft.inference.tables.memory}
                                    getValue={(item) => item.id}
                                    getTitle={(item) => item.name}
                                    getSort={() => new SortItem(namirasoft.inference.tables.memory, namirasoft.inference.tables.memory.columns.name, true)}
                                    onChanged={this.onChanged}
                                    style={{ width: NSBox.width.one }}
                                />
                                <NSBoxInteger
                                    ref={this.NSBoxInteger_MemoryLimit}
                                    title="Memory Limit"
                                    required={false}
                                    disabled={this.props.disabled}
                                    placeholder="10"
                                    min={0}
                                    onChanged={this.onChanged}
                                    style={{ width: NSBox.width.one }}
                                />
                                <NSBoxEntity<KnowledgebaseRow>
                                    ref={this.NSBoxEntity_Knowledgebases}
                                    {...this.props}
                                    title="Knowledgebases"
                                    required={false}
                                    multiple
                                    clearable
                                    table={namirasoft.inference.tables.knowledgebase}
                                    getValue={(item) => item.id}
                                    getTitle={(item) => item.name}
                                    getSort={() => new SortItem(namirasoft.inference.tables.knowledgebase, namirasoft.inference.tables.knowledgebase.columns.name, true)}
                                    onChanged={this.onChanged}
                                    style={{ width: NSBox.width.double }}
                                />
                            </NSRow>
                            <NSRow>
                                <NSBoxEntity<BudgetRow>
                                    ref={this.NSBoxEntity_Budgets}
                                    {...this.props}
                                    title="Budgets"
                                    required={false}
                                    multiple
                                    clearable
                                    table={namirasoft.inference.tables.budget}
                                    getValue={(item) => item.id}
                                    getTitle={(item) => item.name}
                                    getSort={() => new SortItem(namirasoft.inference.tables.budget, namirasoft.inference.tables.budget.columns.name, true)}
                                    onChanged={this.onChanged}
                                    style={{ width: NSBox.width.double }}
                                />
                            </NSRow>
                            <NSRow>
                                <NSRepeater<ChatTagLine, ChatTagValue>
                                    ref={this.NSRepeater_Tags}
                                    title="Tags"
                                    required={false}
                                    disabled={this.props.disabled}
                                    createItem={(gIndex: number, lIndex: number, ref: React.RefObject<ChatTagLine>, onChange: () => void, onDelete: undefined | ((index: number) => void)) =>
                                    {
                                        return <ChatTagLine
                                            ref={ref}
                                            {...this.props}
                                            key={`ChatTagLine_${gIndex}`}
                                            index={lIndex}
                                            deletable={true}
                                            onChanged={() => { onChange(); }}
                                            onDelete={() => { onDelete?.(lIndex); }}
                                        />;
                                    }}
                                    isEmpty={function (_, value: ChatTagValue | null): boolean
                                    {
                                        return !ChatTagLine.IS_DIRTY(value);
                                    }}
                                    getValue={function (item: ChatTagLine, checkError: boolean): ChatTagValue | null
                                    {
                                        return item.getValue(checkError);
                                    }}
                                    setValue={function (item: ChatTagLine, _: number, value: ChatTagValue | null): void
                                    {
                                        if (value)
                                            item.setValue(value);
                                    }}
                                    onValuesChanged={this.onChanged}
                                    style={{ width: NSBox.width.double }}
                                />
                            </NSRow>
                        </NSColumn>
                    </div>
                }
            </div>
        );
    }
}